import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchLobbySections, type LobbySections } from '../api/catalog';
import { listRecent } from '../api/playerGames';
import type { Game } from '../api/types';
import { CategoryChips, type GameCategory } from '../components/CategoryChips';
import { GameGrid } from '../components/GameGrid';
import { GameSectionRow } from '../components/GameSectionRow';
import { SearchBar } from '../components/SearchBar';
import { useAuth } from '../auth/AuthContext';
import { useCatalog } from '../hooks/useCatalog';
import { restoreCatalogPosition } from '../lib/gameLaunch';

const EMPTY_SECTIONS: LobbySections = {
  catalog_total: 0,
  slots: [],
  popular: [],
  recommended: [],
  new: [],
  fast: [],
  bonus: [],
};

type SectionKey =
  | 'all'
  | 'slots'
  | 'popular'
  | 'recommended'
  | 'new'
  | 'fast'
  | 'bonus'
  | 'history';

function parseSection(raw: string | null): SectionKey {
  const value = (raw || '').toLowerCase();
  if (
    value === 'slots' ||
    value === 'popular' ||
    value === 'recommended' ||
    value === 'new' ||
    value === 'fast' ||
    value === 'bonus' ||
    value === 'history'
  ) {
    return value;
  }
  return 'all';
}

export function GamesPage() {
  const { t } = useTranslation(['games', 'common']);
  const { user } = useAuth();
  const [params] = useSearchParams();
  const section = parseSection(params.get('section') || params.get('category'));
  const catalog = useCatalog();
  const [sections, setSections] = useState<LobbySections>(EMPTY_SECTIONS);
  const [history, setHistory] = useState<Game[]>([]);

  useEffect(() => {
    restoreCatalogPosition();
  }, []);

  useEffect(() => {
    if (section === 'slots') catalog.setCategory('slots');
    else if (section === 'all') catalog.setCategory('all');
  }, [section]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    void fetchLobbySections()
      .then((data) => setSections(data))
      .catch(() => setSections(EMPTY_SECTIONS));
  }, []);

  useEffect(() => {
    if (!user) {
      setHistory([]);
      return;
    }
    let cancelled = false;
    void listRecent()
      .then((data) => {
        const resolved: Game[] = [];
        for (const entry of data.recent || []) {
          if (entry.game) resolved.push(entry.game);
          if (resolved.length >= 48) break;
        }
        if (!cancelled) setHistory(resolved);
      })
      .catch(() => {
        if (!cancelled) setHistory([]);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const category = (catalog.category === 'slots' || section === 'slots' ? 'slots' : 'all') as GameCategory;
  const searching = Boolean(catalog.query.trim());

  const slotGames = sections.slots;
  const popularGames = sections.popular;
  const recommended = sections.recommended;
  const newest = sections.new;
  const fastGames = sections.fast;
  const bonusGames = sections.bonus;

  const sectionTitle = useMemo(() => {
    switch (section) {
      case 'slots':
        return t('slots');
      case 'popular':
        return t('popular');
      case 'recommended':
        return t('recommended', { defaultValue: 'Tövsiyə olunanlar' });
      case 'new':
        return t('new');
      case 'fast':
        return t('fastGames', { defaultValue: 'Sürətli oyunlar' });
      case 'bonus':
        return t('bonusWager', { defaultValue: 'Bonusla mərc' });
      case 'history':
        return t('history', { defaultValue: 'Oyun tarixçəsi' });
      default:
        return t('title');
    }
  }, [section, t]);

  const sectionGames = useMemo(() => {
    switch (section) {
      case 'slots':
        return slotGames;
      case 'popular':
        return popularGames;
      case 'recommended':
        return recommended;
      case 'new':
        return newest;
      case 'fast':
        return fastGames;
      case 'bonus':
        return bonusGames;
      case 'history':
        return history;
      default:
        return category === 'slots' ? slotGames : catalog.games;
    }
  }, [
    section,
    slotGames,
    popularGames,
    recommended,
    newest,
    fastGames,
    bonusGames,
    history,
    category,
    catalog.games,
  ]);

  const focusedSection = section !== 'all';
  const displayCount = focusedSection
    ? sectionGames.length
    : category === 'slots'
      ? slotGames.length
      : catalog.total;

  return (
    <div className="page casino-page">
      <div className="section-head">
        <div>
          <h2>{sectionTitle}</h2>
          {!focusedSection ? <p>{t('subtitle')}</p> : null}
        </div>
        <span className="muted-note">{t('results', { count: displayCount })}</span>
      </div>

      <SearchBar value={catalog.query} onChange={catalog.setQuery} />
      {!searching && !focusedSection ? (
        <CategoryChips value={category} onChange={(value) => catalog.setCategory(value)} />
      ) : null}

      {searching ? (
        <div className="casino-all">
          <div className="section-head">
            <h2>{t('searchResults', { defaultValue: 'Axtarış' })}</h2>
          </div>
          <GameGrid
            games={catalog.games}
            loading={catalog.gameLoading}
            error={catalog.error}
            emptyMessage={catalog.message}
            onRetry={catalog.reload}
          />
          {!catalog.gameLoading && catalog.hasMore ? (
            <div className="catalog-load-more">
              <button
                type="button"
                className="btn btn-ghost"
                disabled={catalog.loadingMore}
                onClick={catalog.loadMore}
              >
                {catalog.loadingMore ? t('loadingMore') : t('loadMore')}
              </button>
            </div>
          ) : null}
        </div>
      ) : focusedSection ? (
        <div className="casino-all">
          <GameGrid
            games={sectionGames}
            loading={catalog.gameLoading && section !== 'history'}
            error={catalog.error}
            emptyMessage={
              section === 'history' && !user
                ? t('historyLogin', { defaultValue: 'Tarixçə üçün daxil olun' })
                : t('emptyTitle')
            }
            onRetry={catalog.reload}
          />
          {!catalog.gameLoading && catalog.hasMore && section === 'slots' ? (
            <div className="catalog-load-more">
              <button
                type="button"
                className="btn btn-ghost"
                disabled={catalog.loadingMore}
                onClick={catalog.loadMore}
              >
                {catalog.loadingMore ? t('loadingMore') : t('loadMore')}
              </button>
            </div>
          ) : null}
        </div>
      ) : (
        <>
          {history.length > 0 ? (
            <GameSectionRow
              title={t('history', { defaultValue: 'Oyun tarixçəsi' })}
              games={history}
              seeAllTo="/games?section=history"
            />
          ) : null}
          {slotGames.length > 0 ? (
            <GameSectionRow title={t('slots')} games={slotGames.slice(0, 36)} seeAllTo="/games?section=slots" />
          ) : null}
          {popularGames.length > 0 ? (
            <GameSectionRow
              title={t('popular')}
              games={popularGames.slice(0, 24)}
              seeAllTo="/games?section=popular"
            />
          ) : null}
          {recommended.length > 0 ? (
            <GameSectionRow
              title={t('recommended', { defaultValue: 'Tövsiyə olunanlar' })}
              games={recommended.slice(0, 24)}
              seeAllTo="/games?section=recommended"
            />
          ) : null}
          {newest.length > 0 ? (
            <GameSectionRow title={t('new')} games={newest.slice(0, 36)} seeAllTo="/games?section=new" />
          ) : null}
          {fastGames.length > 0 ? (
            <GameSectionRow
              title={t('fastGames', { defaultValue: 'Sürətli oyunlar' })}
              games={fastGames.slice(0, 24)}
              seeAllTo="/games?section=fast"
            />
          ) : null}
          {bonusGames.length > 0 ? (
            <GameSectionRow
              title={t('bonusWager', { defaultValue: 'Bonusla mərc' })}
              games={bonusGames.slice(0, 24)}
              seeAllTo="/games?section=bonus"
            />
          ) : null}

          <div className="section-head">
            <h2>{category === 'slots' ? t('slots') : t('lobbySection')}</h2>
            <span className="muted-note">{t('results', { count: displayCount })}</span>
          </div>
          <div className="casino-all">
            <GameGrid
              games={category === 'slots' ? slotGames : catalog.games}
              loading={catalog.gameLoading}
              error={catalog.error}
              emptyMessage={catalog.message}
              onRetry={catalog.reload}
            />
            {!catalog.gameLoading && catalog.hasMore && category === 'all' ? (
              <div className="catalog-load-more">
                <p className="muted-note">
                  {t('showingOf', {
                    shown: catalog.loadedCount,
                    total: catalog.total,
                    defaultValue: '{{shown}} / {{total}} games',
                  })}
                </p>
                <button
                  type="button"
                  className="btn btn-ghost"
                  disabled={catalog.loadingMore}
                  onClick={catalog.loadMore}
                >
                  {catalog.loadingMore ? t('loadingMore') : t('loadMore')}
                </button>
              </div>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}
